import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { officiantService } from '../../../../services/officiantService';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('OfficiantInput');

export const OfficiantInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, { officiantId?: string; isEdit?: boolean }>, string>>();
  const { officiantId, isEdit } = route.params || {};
  const [showDropdown, setShowDropdown] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const [savedOfficiantId, setSavedOfficiantId] = useState<string | undefined>();

  const [formData, setFormData] = useState({
    officiantName: '',
    phoneNumber: '',
    email: '',
    choice: '',
    notes: '',
  });

  const choices = ['First Choice', 'Second Choice', 'Third Choice', 'Last Choice'];

  // Load existing officiant data when editing
  useEffect(() => {
    logger.debug('[Edit Debug] useEffect triggered - isEdit:', isEdit, 'officiantId:', officiantId);
    if (isEdit && officiantId) {
      logger.debug('[Edit Debug] Loading officiant data for editing');
      loadOfficiantData();
    }
  }, [isEdit, officiantId]);

  const loadOfficiantData = async () => {
    try {
      logger.debug('[Edit Debug] loadOfficiantData called with ID:', officiantId);
      if (!officiantId) {
        logger.debug('[Edit Debug] No officiantId provided, returning');
        return;
      }

      logger.debug('[Edit Debug] Fetching data for ID:', officiantId);
      const data = await officiantService.getOfficiantById(officiantId);
      logger.debug('[Edit Debug] Received data:', data);

      if (data) {
        setFormData({
          officiantName: data.officiantName || '',
          phoneNumber: formatPhoneNumber(data.phoneNumber || ''),
          email: data.email || '',
          choice: data.choice || '',
          notes: data.notes || '',
        });
      }
    } catch (error) {
      logger.error('Error loading officiant data:', error);
      Alert.alert('Error', 'Failed to load officiant information');
    }
  };

  // Helper: format phone as (xxx) xxx-xxxx while typing
  const formatPhoneNumber = (text: string) => {
    const cleaned = (text || '').replace(/\D/g, '');
    if (cleaned.length >= 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (cleaned.length >= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length >= 3) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }
    return cleaned;
  };

  // Validate email format
  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handlePhoneChange = (text: string) => {
    const formatted = formatPhoneNumber(text);
    setFormData({ ...formData, phoneNumber: formatted });
  };

  const handleEmailChange = (text: string) => {
    setFormData({ ...formData, email: text.toLowerCase() });
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      // Basic validation
      if (!formData.officiantName) {
        Alert.alert('Validation Error', 'Officiant name is required');
        setSubmitting(false);
        return;
      }

      // Validate email if provided
      if (formData.email && !validateEmail(formData.email)) {
        Alert.alert('Validation Error', 'Please enter a valid email address');
        setSubmitting(false);
        return;
      }

      // Prepare payload for API
      const payload: any = {
        officiantName: formData.officiantName,
        phoneNumber: formData.phoneNumber.replace(/\D/g, '') || undefined,
        email: formData.email || undefined,
        choice: formData.choice || undefined,
        notes: formData.notes || undefined,
      };

      // Create or update officiant record
      let saved;
      let savedId;

      if (isEdit && officiantId) {
        saved = await officiantService.updateOfficiant(officiantId, payload);
        savedId = officiantId;
      } else {
        saved = await officiantService.createOfficiant(payload);
        // Handle wrapped response format { success, data, message }
        const createdData = saved?.data || saved;
        savedId = createdData.id;
      }

      // Store the saved ID for the Done button
      setSavedOfficiantId(savedId);

      if (isEdit) {
        // For edit mode, navigate to lock/unlock screen
        Alert.alert('Success', 'Officiant information updated successfully!', [
          {
            text: 'OK',
            onPress: () => navigation.navigate('OfficiantLockUnlock', { officiantId: officiantId })
          }
        ]);
      } else {
        // Clear the form and reset states for Add Another
        setFormData({
          officiantName: '',
          phoneNumber: '',
          email: '',
          choice: '',
          notes: '',
        });

        // Show Add Another / Done buttons after successful save
        setShowAddAnotherDone(true);
        // Reset submitting so buttons are interactive
        setSubmitting(false);
      }
    } catch (error: any) {
      logger.error('Error saving officiant info:', error);
      Alert.alert('Error', 'Failed to save officiant information. Please try again.');
      setSubmitting(false);
    }
  };

  const handleAddAnother = () => {
    // Clear form and hide buttons
    setFormData({
      officiantName: '',
      phoneNumber: '',
      email: '',
      choice: '',
      notes: '',
    });
    setShowAddAnotherDone(false);
    setSubmitting(false);
  };

  const handleDone = () => {
    setSubmitting(false);
    // Navigate to lock/unlock with the saved ID
    logger.debug('[Done Debug] Using ID:', savedOfficiantId);
    (navigation as any).navigate('OfficiantLockUnlock', { officiantId: savedOfficiantId });
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>Officiant Info</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Full Name *</Text>
            <TextInput
              style={styles.input}
              value={formData.officiantName}
              onChangeText={(text) => setFormData({ ...formData, officiantName: text })}
              placeholder="Ex: Rev Jim Beam"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Phone Number</Text>
            <TextInput
              style={styles.input}
              value={formData.phoneNumber}
              onChangeText={handlePhoneChange}
              placeholder="Ex: (555) 123-4567"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Email</Text>
            <TextInput
              style={styles.input}
              value={formData.email}
              onChangeText={handleEmailChange}
              placeholder="Ex: jimbeam@gmail.com"
              placeholderTextColor="#A9A9A9"
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Choice</Text>
            <TouchableOpacity
              style={styles.dropdownButton}
              onPress={() => setShowDropdown(!showDropdown)}
            >
              <Text style={styles.dropdownText}>
                {formData.choice || 'Select a choice'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={styles.dropdownIcon}
              />
            </TouchableOpacity>
            {showDropdown && (
              <View style={styles.dropdownList}>
                {choices.map((choice, index) => (
                  <TouchableOpacity
                    key={index}
                    style={styles.dropdownItem}
                    onPress={() => {
                      setFormData({ ...formData, choice: choice });
                      setShowDropdown(false);
                    }}
                  >
                    <Text style={styles.dropdownItemText}>{choice}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.notes}
              onChangeText={(text) => setFormData({ ...formData, notes: text })}
              placeholder="Additional information..."
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
            />
          </View>

          {showAddAnotherDone ? (
            <View style={styles.addAnotherDoneContainer}>
              <TouchableOpacity
                style={styles.addAnotherButton}
                onPress={handleAddAnother}
              >
                <Text style={styles.submitButtonText}>Add Another</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.doneButton}
                onPress={handleDone}
              >
                <Text style={styles.submitButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.submitButtonContainer}>
              {submitting ? (
                <TouchableOpacity style={[styles.submitButton, styles.buttonDisabled]} disabled>
                  <ActivityIndicator color="#FFFFFF" />
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={styles.submitButton}
                  onPress={handleSubmit}
                >
                  <Text style={styles.submitButtonText}>
                    {isEdit ? 'Update' : 'Submit'}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
   backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
    paddingBottom: ms(120),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  addAnotherDoneContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(120),
    gap: ms(20),
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  addAnotherButton: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  dropdownButton: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  dropdownText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#1C2C4F',
  },
  dropdownList: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    marginTop: ms(5),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  dropdownItem: {
    padding: ms(15),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  dropdownItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
});

export default OfficiantInputScreen;
